import { ArrowLeftIcon } from '@heroicons/react/16/solid';
import { classSet } from '@platform/ui-library';
import { FC, useEffect } from 'react';
import { DesignerSetting } from '@app/DesignerSetting';
import { useDesign, useLatestDesign } from '@app/hooks';
import { AppVersioningService } from '@app/services';
import { DesignBranchingEventType } from '@app/types';
import { getEnvironmentInfo, getLatestBranchingEvent } from '@app/utilities';
import { AppNav } from './AppNav';
import styles from './AppHeader.module.scss';

export const AppHeader: FC = () => {
  const serialized = useDesign();
  const latestSerialized = useLatestDesign();
  const isClientView = DesignerSetting.s.is_public === 'true';
  const environment = DesignerSetting.s.env;
  const { isLocal, isProd } = getEnvironmentInfo(environment);
  const branchingInitialized = serialized.branchingMerging?.initialized ?? false;
  const {
    branched_at: branchedAt,
    parent_branch_name: parentBranchName
  } = getLatestBranchingEvent(serialized.branching, DesignBranchingEventType.initBranch) ?? {};

  useEffect(() => {
    window.apm.onValidateApplication();
  }, []);

  const onExitClick = () => {
    const search = new URLSearchParams(location.search);
    search.delete('g');
    const searchString = search.toString();
    window.location.href = (searchString) ? `/?${searchString}` : '/';
  };

  const onLatestVersionClick = () => {
    if (latestSerialized?.version) {
      window.apm.openLatestVersion();
    }
  };

  const onOpenDesignValidation = () => {
    window.apm.openDesignValidation();
  };

  let environmentWarning: string | null = null;
  if (isLocal && isProd) {
    environmentWarning = 'Any changes saved in this environment will be reflected in PROD.';
  } else if (!isLocal && !isProd) {
    environmentWarning = 'This environment is solely for testing D2, saves here will regularly get deleted.';
  }

  return (
    <>
      <div className={styles.mainContainer}>
        <div className={styles.exitButton} onClick={onExitClick}>
          <ArrowLeftIcon />
        </div>
        <div className={styles.designTitle}>{`${serialized.clientName} ${serialized.designName}`}</div>
        <div className={styles.chip}>{serialized.version}</div>
        {(!branchingInitialized) ? null : (
          <div className={classSet(['flex-center', 'flex-gap', styles.branchChips])}>
            <div className={styles.branchNameChip}>
              {serialized.branchName ?? AppVersioningService.mainBranchName}
            </div>
            {(!parentBranchName) ? null : (
              <div className="hover-tooltip-container">
                <div className="hover-tooltip-trigger">
                  <i className="fe icon-info" />
                </div>
                <div className="hover-tooltip">
                  This branch is derived from&nbsp;
                  <strong>{parentBranchName} ({branchedAt})</strong>
                </div>
              </div>
            )}
          </div>
        )}
        <div
          className={classSet(['hidden', styles.validationChip])}
          data-tip="Click to view design issues."
          id="design-validation"
          onClick={onOpenDesignValidation}
        >
          <i className="fe icon-alert-triangle" />
        </div>
        {(!environmentWarning) ? null : (
          <div
            className={styles.environmentChip}
            data-tip={environmentWarning}
          >{`Warning: ${environment}`}</div>
        )}
        {(isClientView) ? null : <div className={styles.savingStatus} id="saving-status" />}
        {(isClientView) ? null : (
          <div
            className={classSet(['warning', styles.versionInfo])}
            id="version-info"
            onClick={onLatestVersionClick}
          >
            fetching latest version number...
          </div>
        )}
      </div>
      <AppNav />
    </>
  );
};
